import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';

import {
  DiagnosisCustomerCreateDTO,
  DiagnosisCustomerReadDTO,
} from '../../../generated/api-client';
import { useUpdateDiagnosisCustomer } from '../../../hooks';
import { customerFormSchema, type CustomerFormData } from '../../../schemas/customerFormSchema';
import { CustomerDetailMainTab } from '../../customer/CustomerDetailMainTab';
import { DiagnosisStepHandle } from './Step.types';

interface DiagnosisCustomerStepProps {
  diagnosisCustomerId?: string;
  data?: DiagnosisCustomerReadDTO | null;
}

const defaultValues: CustomerFormData = {
  company_name: '',
  ateco_2025: '',
  address: '',
  phone: '',
  voluntary: null,
  type: '',
  multi_site: null,
  clustering: '',
  employee_count: null,
  revenue: null,
  balance_sheet_assets: null,
  size_category: '',
  consumption_category: '',
  average_annual_consumption: null,
  high_electricity_consumption: null,
  high_gas_consumption: null,
  certifications: '',
  vat_number: '',
  tax_code: '',
  certified_email: '',
  legal_representative: '',
  legal_representative_phone: '',
  legal_representative_email: '',
  contact_person: '',
  contact_person_phone: '',
  contact_person_email: '',
};

const emptyToUndefined = (value: string | null | undefined) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const toNullableNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const mapDiagnosisCustomerToForm = (
  entity?: DiagnosisCustomerReadDTO | null
): CustomerFormData => ({
  company_name: entity?.company_name ?? '',
  ateco_2025: entity?.ateco_2025 ?? '',
  address: entity?.address ?? '',
  phone: entity?.phone ?? '',
  voluntary: entity?.voluntary ?? null,
  type: entity?.type ?? '',
  multi_site: entity?.single_multi_site ?? null,
  clustering: entity?.clustering ?? '',
  employee_count: entity?.employee_count ?? null,
  revenue: entity?.revenue ?? null,
  balance_sheet_assets: entity?.balance_sheet_assets ?? null,
  size_category: entity?.size_category ?? '',
  consumption_category: entity?.consumption_category ?? '',
  average_annual_consumption: entity?.average_annual_consumption ?? null,
  high_electricity_consumption: entity?.high_electricity_consumption ?? undefined,
  high_gas_consumption: entity?.high_gas_consumption ?? undefined,
  certifications: entity?.certifications ?? '',
  vat_number: entity?.vat_number ?? '',
  tax_code: entity?.tax_code ?? '',
  certified_email: entity?.certified_email ?? '',
  legal_representative: entity?.legal_representative ?? '',
  legal_representative_phone: entity?.legal_representative_phone ?? '',
  legal_representative_email: entity?.legal_representative_email ?? '',
  contact_person: entity?.diagnosis_contact_person ?? '',
  contact_person_phone: entity?.contact_person_phone ?? '',
  contact_person_email: entity?.contact_person_email ?? '',
});

const mapFormToDiagnosisCustomerDTO = (form: CustomerFormData): DiagnosisCustomerCreateDTO =>
  new DiagnosisCustomerCreateDTO({
    company_name: form.company_name.trim(),
    ateco_2025: emptyToUndefined(form.ateco_2025),
    address: emptyToUndefined(form.address),
    phone: emptyToUndefined(form.phone),
    voluntary:
      form.voluntary === null
        ? (null as unknown as boolean)
        : form.voluntary === undefined
          ? undefined
          : form.voluntary,
    type: emptyToUndefined(form.type),
    single_multi_site:
      form.multi_site === null
        ? (null as unknown as boolean)
        : form.multi_site === undefined
          ? undefined
          : form.multi_site,
    clustering: emptyToUndefined(form.clustering),
    employee_count: toNullableNumber(form.employee_count),
    revenue: toNullableNumber(form.revenue),
    balance_sheet_assets: toNullableNumber(form.balance_sheet_assets),
    size_category: emptyToUndefined(form.size_category),
    consumption_category: emptyToUndefined(form.consumption_category),
    average_annual_consumption: toNullableNumber(form.average_annual_consumption),
    high_electricity_consumption:
      form.high_electricity_consumption === null
        ? (null as unknown as boolean)
        : form.high_electricity_consumption === undefined
          ? undefined
          : form.high_electricity_consumption,
    high_gas_consumption:
      form.high_gas_consumption === null
        ? (null as unknown as boolean)
        : form.high_gas_consumption === undefined
          ? undefined
          : form.high_gas_consumption,
    certifications: emptyToUndefined(form.certifications),
    vat_number: emptyToUndefined(form.vat_number),
    tax_code: emptyToUndefined(form.tax_code),
    certified_email: emptyToUndefined(form.certified_email),
    legal_representative: emptyToUndefined(form.legal_representative),
    legal_representative_phone: emptyToUndefined(form.legal_representative_phone),
    legal_representative_email: emptyToUndefined(form.legal_representative_email),
    diagnosis_contact_person: emptyToUndefined(form.contact_person),
    contact_person_phone: emptyToUndefined(form.contact_person_phone),
    contact_person_email: emptyToUndefined(form.contact_person_email),
  });

export const DiagnosisCustomerStep = forwardRef<DiagnosisStepHandle, DiagnosisCustomerStepProps>(
  ({ diagnosisCustomerId, data }, ref) => {
    const formMethods = useForm<CustomerFormData>({
      defaultValues,
      resolver: zodResolver(customerFormSchema),
      mode: 'onChange',
    });

    // Watch tutti i campi per forzare il re-render quando cambiano
    formMethods.watch();

    const updateDiagnosisCustomer = useUpdateDiagnosisCustomer();

    useEffect(() => {
      if (data) {
        formMethods.reset(mapDiagnosisCustomerToForm(data));
      } else {
        formMethods.reset(defaultValues);
      }
    }, [data, formMethods]);

    const performSave = async (formData: CustomerFormData) => {
      if (!diagnosisCustomerId) {
        return { success: true };
      }
      try {
        const payload = mapFormToDiagnosisCustomerDTO(formData);
        await updateDiagnosisCustomer.mutateAsync({
          id: diagnosisCustomerId,
          data: payload,
        });
        toast.success('Dati cliente diagnosi salvati');
        return { success: true };
      } catch (error: any) {
        const message = error?.message || 'Errore nel salvataggio del cliente';
        toast.error(message);
        return { success: false };
      }
    };

    React.useImperativeHandle(ref, () => ({
      save: async (force = false) => {
        const isValid = force ? true : await formMethods.trigger(undefined, { shouldFocus: true });
        if (!isValid && !force) {
          return { success: false, requiresConfirmation: true };
        }
        const values = formMethods.getValues();
        return performSave(values);
      },
    }));

    return (
      <FormProvider {...formMethods}>
        <CustomerDetailMainTab />
      </FormProvider>
    );
  }
);

DiagnosisCustomerStep.displayName = 'DiagnosisCustomerStep';
